import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import BasicCli from '../../main/components/basicCli/BasicCli';
import GeneralModal from '../../main/components/modal/general/GeneralModal';
import GeneralInput from '../../main/components/generalInput/GeneralInput';
import axiosInstance from '../../main/initializers/axios';
import { ApiConstants } from '../../main/constants/ApiConstants';
import ISubject from '../../main/interfaces/ISubject';
import '../hotel/Hotel/Hotel.css';
import '../hotel/HotelShared/HotelShared.css';

type SubjectFormModel = ISubject;

const emptySubject: SubjectFormModel = {
  name: '',
  nipt: '',
  code: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  country: '',
  isActive: true,
};

const SubjectsPage: React.FC = () => {
  const { t } = useTranslation();
  const [subjects, setSubjects] = useState<ISubject[]>([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState<SubjectFormModel>(emptySubject);
  const [search, setSearch] = useState('');

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SubjectFormModel>({
    defaultValues: emptySubject,
  });

  const loadSubjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(ApiConstants.invoice.subjects);
      setSubjects(Array.isArray(response.data) ? response.data : []);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('invoice.subjects.messages.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadSubjects();
  }, [loadSubjects]);

  useEffect(() => {
    reset(selectedSubject);
  }, [selectedSubject, reset]);

  const filteredSubjects = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return subjects;

    return subjects.filter((subject) =>
      [subject.name, subject.nipt, subject.code, subject.address, subject.city, subject.country]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [search, subjects]);

  const openCreate = () => {
    setSelectedSubject(emptySubject);
    setOpenModal(true);
  };

  const openEdit = (subject: ISubject) => {
    setSelectedSubject({
      ...emptySubject,
      ...subject,
      isActive: subject.isActive ?? true,
    });
    setOpenModal(true);
  };

  const onSave = handleSubmit(async (formData) => {
    try {
      await axiosInstance.post(ApiConstants.invoice.subjects, formData);
      toast.success(formData.id ? t('invoice.subjects.messages.updated') : t('invoice.subjects.messages.created'));
      setOpenModal(false);
      setSelectedSubject(emptySubject);
      await loadSubjects();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('invoice.subjects.messages.saveError'));
    }
  });

  const onDelete = async (subject: ISubject) => {
    if (!subject.id) return;
    if (!window.confirm(t('invoice.subjects.deleteConfirm', { name: subject.name }))) return;

    try {
      await axiosInstance.delete(`${ApiConstants.invoice.subjects}?id=${subject.id}`);
      toast.success(t('invoice.subjects.messages.deleted'));
      await loadSubjects();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('invoice.subjects.messages.deleteError'));
    }
  };

  return (
    <BasicCli>
      <div className="hotelSectionPage">
        <div className="ui-panel hotelSectionHeader">
          <div className="hotelSectionHeading">
            <h2>{t('invoice.subjects.title')}</h2>
            <p>{t('invoice.subjects.subtitle')}</p>
          </div>

          <div className="hotelSectionActions" style={{ gap: 12 }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('invoice.subjects.searchPlaceholder')}
              style={{
                minWidth: 260,
                padding: '10px 14px',
                borderRadius: 12,
                border: '1px solid #d6d6d6',
              }}
            />
            <Button variant="contained" onClick={openCreate}>
              {t('invoice.subjects.addButton')}
            </Button>
          </div>
        </div>

        <div className="bcCard hotelSectionCard hotelTableCard">
          {loading ? (
            <div style={{ padding: 24 }}>{t('invoice.subjects.loading')}</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>{t('invoice.subjects.columns.name')}</th>
                    <th style={thStyle}>{t('invoice.subjects.columns.nipt')}</th>
                    <th style={thStyle}>{t('invoice.subjects.columns.address')}</th>
                    <th style={thStyle}>{t('invoice.subjects.columns.contact')}</th>
                    <th style={thStyle}>{t('invoice.subjects.columns.status')}</th>
                    <th style={thStyle}>{t('invoice.subjects.columns.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubjects.map((subject) => (
                    <tr key={subject.id}>
                      <td style={tdStyle}>
                        <strong>{subject.name}</strong>
                        {subject.code ? <div style={subtleStyle}>{t('invoice.subjects.codeLabel')}: {subject.code}</div> : null}
                      </td>
                      <td style={tdStyle}>{subject.nipt}</td>
                      <td style={tdStyle}>
                        <div>{subject.address}</div>
                        {(subject.city || subject.country) && (
                          <div style={subtleStyle}>
                            {[subject.city, subject.country].filter(Boolean).join(', ')}
                          </div>
                        )}
                      </td>
                      <td style={tdStyle}>
                        <div>{subject.email || '-'}</div>
                        <div style={subtleStyle}>{subject.phone || '-'}</div>
                      </td>
                      <td style={tdStyle}>
                        <span style={statusStyle(subject.isActive ?? true)}>
                          {subject.isActive ? t('invoice.subjects.status.active') : t('invoice.subjects.status.inactive')}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Button size="small" variant="outlined" onClick={() => openEdit(subject)}>
                            {t('actions.edit')}
                          </Button>
                          <Button size="small" color="error" variant="outlined" onClick={() => onDelete(subject)}>
                            {t('actions.delete')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!filteredSubjects.length && (
                    <tr>
                      <td style={tdStyle} colSpan={6}>
                        {t('invoice.subjects.empty')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {openModal && (
          <GeneralModal
            handleClose={() => setOpenModal(false)}
            headerName={selectedSubject.id ? t('invoice.subjects.modal.editTitle') : t('invoice.subjects.modal.createTitle')}
            width="920px"
            radius="16px"
            hideFooter
          >
            <form className="hotelForm" onSubmit={onSave}>
              <div className="hotelGrid">
                <GeneralInput label={t('invoice.subjects.fields.name')} name="name" type="text" control={control} required errors={errors} placeholder={t('invoice.subjects.fields.name')} />
                <GeneralInput label={t('invoice.subjects.fields.nipt')} name="nipt" type="text" control={control} required errors={errors} placeholder={t('invoice.subjects.fields.nipt')} />
                <GeneralInput label={t('invoice.subjects.fields.code')} name="code" type="text" control={control} errors={errors} placeholder={t('invoice.subjects.fields.code')} />
                <GeneralInput label={t('invoice.subjects.fields.address')} name="address" type="text" control={control} required errors={errors} placeholder={t('invoice.subjects.fields.address')} />
                <GeneralInput label={t('invoice.subjects.fields.email')} name="email" type="text" control={control} errors={errors} placeholder={t('invoice.subjects.fields.email')} />
                <GeneralInput label={t('invoice.subjects.fields.phone')} name="phone" type="text" control={control} errors={errors} placeholder={t('invoice.subjects.fields.phone')} />
                <GeneralInput label={t('invoice.subjects.fields.city')} name="city" type="text" control={control} errors={errors} placeholder={t('invoice.subjects.fields.city')} />
                <GeneralInput label={t('invoice.subjects.fields.country')} name="country" type="text" control={control} errors={errors} placeholder={t('invoice.subjects.fields.country')} />
              </div>

              <div className="hotelImageField" style={{ marginTop: 16 }}>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontWeight: 600 }}>
                      <input type="checkbox" checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
                      <span>{t('invoice.subjects.fields.active')}</span>
                    </label>
                  )}
                />
              </div>

              <div className="hotelActions">
                <Button variant="outlined" onClick={() => setOpenModal(false)}>
                  {t('buttons.cancel')}
                </Button>
                <Button type="submit" variant="contained">
                  {t('actions.save')}
                </Button>
              </div>
            </form>
          </GeneralModal>
        )}
      </div>
    </BasicCli>
  );
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '14px 16px',
  borderBottom: '1px solid #ececec',
  color: '#5b5b5b',
  fontSize: 13,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const tdStyle: React.CSSProperties = {
  padding: '14px 16px',
  borderBottom: '1px solid #f1f1f1',
  verticalAlign: 'top',
};

const subtleStyle: React.CSSProperties = {
  fontSize: 13,
  color: '#7a7a7a',
  marginTop: 4,
};

const statusStyle = (active: boolean): React.CSSProperties => ({
  display: 'inline-flex',
  padding: '6px 10px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 700,
  background: active ? '#e8f7ee' : '#f6e9e9',
  color: active ? '#16794a' : '#a43e3e',
});

export default SubjectsPage;
